import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Table, Button, Space, Card, Modal, Form, Input, InputNumber, Select, Tag, Popconfirm, 
  message, Row, Col, Tabs, Statistic, Tooltip, Badge, Segmented, Divider, DatePicker, Upload, Switch 
} from 'antd';
import { 
  DollarOutlined, BankOutlined, PlusOutlined, EditOutlined, LockOutlined,
  SearchOutlined, ReloadOutlined, DownloadOutlined, PrinterOutlined,
  CheckCircleOutlined, ClockCircleOutlined, UserOutlined, FileTextOutlined, CreditCardOutlined,
  SettingOutlined, UploadOutlined, UndoOutlined, UnorderedListOutlined, EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { fetchAcademicYears } from '../../../Service/Syllabus_API/syllabusApi';
import { fetchPaymentsList, savePaymentAdjustment, processSalaryPayment } from '../../../Service/paymentApi';
import logoGif from '../../../Assets/vedika_logo.gif';

const { Option } = Select;

// Helper to convert number to words
const numberToWords = (num) => {
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if ((num = num.toString()).length > 9) return 'Overflow';
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) + 'Only' : 'Only';
  return str;
};

const getCurrentMonthString = () => dayjs().format('YYYY-MM');

const PaymentManagementPage = () => {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearFilter, setAcademicYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState(getCurrentMonthString());
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);

  const [paymentData, setPaymentData] = useState({
    summary: { total_instructors: 0, total_staff: 0, total_net_payable: 0, total_paid: 0, total_pending: 0 },
    instructors: [],
    staff: []
  });

  // Pay Recorded & Pay Tech Cancelled Sessions Toggle States for Instructors
  const [payRecordedMap, setPayRecordedMap] = useState({});
  const [globalPayRecorded, setGlobalPayRecorded] = useState(false);

  const [payTechCancelledMap, setPayTechCancelledMap] = useState({});
  const [globalPayTechCancelled, setGlobalPayTechCancelled] = useState(true);

  // Session-Wise Manual Overrides State (Map of daily_session_id -> boolean)
  const [sessionOverrideMap, setSessionOverrideMap] = useState({});

  // Session Details Inspection Modal State
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [sessionModalInstructor, setSessionModalInstructor] = useState(null);

  // Modal States
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isSalarySlipModalOpen, setIsSalarySlipModalOpen] = useState(false);

  // Salary Slip Custom Header & Logo Customization States
  const [slipOrgName, setSlipOrgName] = useState('VEDIKA ACADEMY');
  const [slipSubtitle, setSlipSubtitle] = useState('Official Payslip / Payment Advice Voucher');
  const [slipCustomLogo, setSlipCustomLogo] = useState('');
  const [showSlipHeaderConfig, setShowSlipHeaderConfig] = useState(false);

  const handleLogoFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        message.error('Logo image size should be less than 2MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        setSlipCustomLogo(uploadEvent.target.result);
        message.success('Custom logo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const [activeRecord, setActiveRecord] = useState(null);
  const [slipRecord, setSlipRecord] = useState(null);

  const [adjustmentForm] = Form.useForm();
  const [payForm] = Form.useForm();
  const slipPrintRef = useRef(null);

  // Load initial academic years
  useEffect(() => {
    const loadYears = async () => {
      try {
        const ayData = await fetchAcademicYears();
        setAcademicYears(ayData);
        const active = ayData.find(ay => ay.status === 'Active' || ay.status === 'ACTIVE');
        if (active) {
          setAcademicYearFilter(String(active.academic_year_id));
        }
      } catch (e) {
        console.error('Failed to load academic years', e);
      }
    };
    loadYears();
  }, []);

  // Load payment list data
  const loadPayments = async () => {
    setLoading(true);
    try {
      const data = await fetchPaymentsList(academicYearFilter, monthFilter, searchText);
      setPaymentData(data);
    } catch (e) {
      message.error(e.message || 'Failed to fetch payments data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (monthFilter) {
      loadPayments();
    }
  }, [academicYearFilter, monthFilter, searchText]);

  // Global toggle handlers
  const handleToggleGlobalPayRecorded = (checked) => {
    setGlobalPayRecorded(checked);
    const newMap = {};
    (paymentData.instructors || []).forEach(inst => {
      newMap[inst.payee_id] = checked;
    });
    setPayRecordedMap(newMap);
  };

  const handleToggleGlobalPayTechCancelled = (checked) => {
    setGlobalPayTechCancelled(checked);
    const newMap = {};
    (paymentData.instructors || []).forEach(inst => {
      newMap[inst.payee_id] = checked;
    });
    setPayTechCancelledMap(newMap);
  };

  // Helper to check if a specific session is paid
  const isSessionPaid = (sess, instPayeeId) => {
    if (!sess) return false;
    const sessId = sess.daily_session_id;

    // 1. Session-wise manual override takes top priority
    if (sessionOverrideMap[sessId] !== undefined) {
      return !!sessionOverrideMap[sessId];
    }

    // 2. Default fallback based on bulk toggles and session type
    const isPayRecorded = payRecordedMap[instPayeeId] !== undefined 
      ? !!payRecordedMap[instPayeeId] 
      : globalPayRecorded;

    const isPayTechCancelled = payTechCancelledMap[instPayeeId] !== undefined 
      ? !!payTechCancelledMap[instPayeeId] 
      : globalPayTechCancelled;

    if (sess.is_cancelled) {
      if (sess.is_technical_cancelled) {
        return isPayTechCancelled;
      }
      return false; // Regular cancelled session default 0 / false
    }

    if (sess.is_recorded) {
      return isPayRecorded;
    }

    return true; // Live session default true
  };

  // Helper to compute effective instructor calculation based on session-wise & bulk toggles
  const getEffectiveInstructorRecord = (record) => {
    if (!record || record.payee_type !== 'INSTRUCTOR') return record;

    const isPayRecorded = payRecordedMap[record.payee_id] !== undefined 
      ? !!payRecordedMap[record.payee_id] 
      : globalPayRecorded;

    const isPayTechCancelled = payTechCancelledMap[record.payee_id] !== undefined 
      ? !!payTechCancelledMap[record.payee_id] 
      : globalPayTechCancelled;

    if (record.is_locked) {
      return {
        ...record,
        isPayRecorded,
        isPayTechCancelled,
        effectiveBaseAmount: record.base_amount,
        effectiveSessionsCount: record.total_sessions,
        effectiveNetPayable: record.net_payable
      };
    }

    const sessions = record.sessions || [];

    let effectiveBaseAmount = 0;
    let effectiveSessionsCount = 0;

    if (sessions.length > 0) {
      sessions.forEach(sess => {
        if (isSessionPaid(sess, record.payee_id)) {
          const units = Number(sess.session_count || 1);
          const rate = parseFloat(sess.session_rate || 0);
          effectiveSessionsCount += units;
          effectiveBaseAmount += (units * rate);
        }
      });
    } else {
      const liveCount = record.sessions_delivered_live || 0;
      const recCount = record.sessions_delivered_recorded || 0;
      const techCancelledCount = record.sessions_cancelled_technical || 0;

      const liveAmt = parseFloat(record.live_session_amount) || 0;
      const recAmt = parseFloat(record.recorded_session_amount) || 0;
      const techCancelledAmt = parseFloat(record.technical_cancelled_session_amount) || 0;

      effectiveSessionsCount = liveCount + (isPayRecorded ? recCount : 0) + (isPayTechCancelled ? techCancelledCount : 0);
      effectiveBaseAmount = liveAmt + (isPayRecorded ? recAmt : 0) + (isPayTechCancelled ? techCancelledAmt : 0);
    }

    effectiveBaseAmount = parseFloat(effectiveBaseAmount.toFixed(2));
    const additionAmount = parseFloat(record.addition_amount) || 0;
    const grossForTds = effectiveBaseAmount + additionAmount;
    const tdsPercent = parseFloat(record.tds_percent) || 0;
    const tdsAmount = parseFloat((grossForTds * (tdsPercent / 100)).toFixed(2));
    const deductionAmount = parseFloat(record.deduction_amount) || 0;
    const effectiveNetPayable = Math.max(0, parseFloat((grossForTds - (tdsAmount + deductionAmount)).toFixed(2)));

    return {
      ...record,
      isPayRecorded,
      isPayTechCancelled,
      base_amount: effectiveBaseAmount,
      total_sessions: effectiveSessionsCount,
      tds_amount: tdsAmount,
      net_payable: effectiveNetPayable
    };
  };

  // Dynamic Summary Totals
  const computedSummary = useMemo(() => {
    let totalNetPayable = 0;
    let totalPaid = 0;
    let totalPending = 0;

    (paymentData.instructors || []).forEach(inst => {
      const eff = getEffectiveInstructorRecord(inst);
      totalNetPayable += eff.net_payable;
      if (eff.is_locked) {
        totalPaid += eff.net_payable;
      } else {
        totalPending += eff.net_payable;
      }
    });

    (paymentData.staff || []).forEach(stf => {
      totalNetPayable += stf.net_payable;
      if (stf.is_locked) {
        totalPaid += stf.net_payable;
      } else {
        totalPending += stf.net_payable;
      }
    });

    return {
      total_instructors: paymentData.instructors.length,
      total_staff: paymentData.staff.length,
      total_net_payable: totalNetPayable,
      total_paid: totalPaid,
      total_pending: totalPending
    };
  }, [paymentData, payRecordedMap, globalPayRecorded, payTechCancelledMap, globalPayTechCancelled, sessionOverrideMap]);

  // Open Session Inspection Modal
  const openSessionModal = (record) => {
    setSessionModalInstructor(record);
    setIsSessionModalOpen(true);
  };

  // Open Adjustment Modal
  const openAdjustmentModal = (record) => {
    if (record.is_locked) {
      message.warning('This salary payment is already PAID and locked. Changes cannot be made.');
      return;
    }
    const effRecord = record.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(record) : record;
    setActiveRecord(effRecord);
    adjustmentForm.setFieldsValue({
      base_amount: effRecord.base_amount,
      addition_amount: effRecord.addition_amount || 0,
      addition_reason: effRecord.addition_reason || '',
      tds_percent: effRecord.tds_percent || 0,
      deduction_amount: effRecord.deduction_amount || 0,
      deduction_reason: effRecord.deduction_reason || ''
    });
    setIsAdjustmentModalOpen(true);
  };

  // Submit Adjustment Form
  const handleSaveAdjustment = async (values) => {
    if (!activeRecord) return;
    try {
      const payload = {
        payee_type: activeRecord.payee_type,
        payee_id: activeRecord.payee_id,
        academic_year_id: activeRecord.academic_year_id,
        payment_month: activeRecord.payment_month,
        base_amount: values.base_amount !== undefined ? values.base_amount : activeRecord.base_amount,
        addition_amount: values.addition_amount || 0,
        addition_reason: values.addition_reason || null,
        tds_percent: values.tds_percent || 0,
        deduction_amount: values.deduction_amount || 0,
        deduction_reason: values.deduction_reason || null
      };

      await savePaymentAdjustment(payload);
      message.success('Expense / Deduction adjustments saved successfully.');
      setIsAdjustmentModalOpen(false);
      loadPayments();
    } catch (e) {
      message.error(e.message || 'Failed to save adjustments.');
    }
  };

  // Open Pay Modal
  const openPayModal = (record) => {
    if (record.is_locked) {
      message.warning('This salary payment is already PAID and locked.');
      return;
    }
    const effRecord = record.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(record) : record;
    setActiveRecord(effRecord);
    payForm.setFieldsValue({
      transaction_id: effRecord.transaction_id || '',
      payment_method: effRecord.payment_method || 'Bank Transfer (NEFT)',
      payment_date: dayjs(),
      paid_by_name: 'Accounts Department'
    });
    setIsPayModalOpen(true);
  };

  // Submit Process Pay Form
  const handleProcessPay = async (values) => {
    if (!activeRecord) return;
    try {
      const payload = {
        payee_type: activeRecord.payee_type,
        payee_id: activeRecord.payee_id,
        academic_year_id: activeRecord.academic_year_id,
        payment_month: activeRecord.payment_month,
        base_amount: activeRecord.base_amount,
        addition_amount: activeRecord.addition_amount,
        addition_reason: activeRecord.addition_reason,
        tds_percent: activeRecord.tds_percent,
        deduction_amount: activeRecord.deduction_amount,
        deduction_reason: activeRecord.deduction_reason,
        transaction_id: values.transaction_id.trim(),
        payment_method: values.payment_method,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        paid_by_name: values.paid_by_name || 'Accounts Department'
      };

      await processSalaryPayment(payload);
      message.success(`Salary payment of ₹${activeRecord.net_payable.toLocaleString('en-IN')} marked as PAID and locked!`);
      setIsPayModalOpen(false);
      loadPayments();
    } catch (e) {
      message.error(e.message || 'Failed to process payment.');
    }
  };

  // Open Salary Slip Modal
  const openSalarySlipModal = (record) => {
    const effRecord = record.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(record) : record;
    setSlipRecord(effRecord);
    setIsSalarySlipModalOpen(true);
  };

  // CSV Export helper for Bank Payouts
  const exportBankCSV = (payeeType) => {
    const rawList = payeeType === 'INSTRUCTOR' ? paymentData.instructors : paymentData.staff;
    if (!rawList || rawList.length === 0) {
      message.warning(`No ${payeeType.toLowerCase()} records to export.`);
      return;
    }

    const list = payeeType === 'INSTRUCTOR' 
      ? rawList.map(r => getEffectiveInstructorRecord(r)) 
      : rawList;

    const headers = payeeType === 'INSTRUCTOR' ? [
      'Payee Type', 'Code / ID', 'Payee Name', 'Bank Account Name', 'Bank Name', 
      'Account Number', 'IFSC Code', 'Branch Name', 'PAN Number', 
      'Live Sessions', 'Recorded Sessions', 'Tech Cancelled Sessions', 'Regular Cancelled Sessions', 'Total Payable Sessions',
      'Pay Recorded Enabled', 'Pay Tech Issue Enabled',
      'Base / Session Amount (INR)', 'Additions / Expenses (INR)', 'TDS Deduction (INR)', 'Other Deductions (INR)', 
      'Net Payable Amount (INR)', 'Payment Status', 'Transaction ID / Ref', 'Payment Method'
    ] : [
      'Payee Type', 'Code / ID', 'Payee Name', 'Bank Account Name', 'Bank Name', 
      'Account Number', 'IFSC Code', 'Branch Name', 'PAN Number', 
      'Base / Session Amount (INR)', 'Additions / Expenses (INR)', 'TDS Deduction (INR)', 'Other Deductions (INR)', 
      'Net Payable Amount (INR)', 'Payment Status', 'Transaction ID / Ref', 'Payment Method'
    ];

    const csvRows = [headers.join(',')];

    list.forEach(row => {
      const csvLine = payeeType === 'INSTRUCTOR' ? [
        row.payee_type,
        `"${row.payee_code}"`,
        `"${(row.payee_name || '').replace(/"/g, '""')}"`,
        `"${(row.bank_account_name || '').replace(/"/g, '""')}"`,
        `"${(row.bank_name || '').replace(/"/g, '""')}"`,
        `"${(row.account_number || '').replace(/"/g, '""')}"`,
        `"${(row.ifsc_code || '').replace(/"/g, '""')}"`,
        `"${(row.branch_name || '').replace(/"/g, '""')}"`,
        `"${(row.pan_number || '').replace(/"/g, '""')}"`,
        row.sessions_delivered_live || 0,
        row.sessions_delivered_recorded || 0,
        row.sessions_cancelled_technical || 0,
        row.sessions_cancelled_regular || 0,
        row.total_sessions || 0,
        row.isPayRecorded ? 'Yes' : 'No',
        row.isPayTechCancelled ? 'Yes' : 'No',
        (row.base_amount || 0).toFixed(2),
        (row.addition_amount || 0).toFixed(2),
        (row.tds_amount || 0).toFixed(2),
        (row.deduction_amount || 0).toFixed(2),
        (row.net_payable || 0).toFixed(2),
        row.payment_status,
        `"${row.transaction_id || ''}"`,
        `"${row.payment_method || ''}"`
      ] : [
        row.payee_type,
        `"${row.payee_code}"`,
        `"${(row.payee_name || '').replace(/"/g, '""')}"`,
        `"${(row.bank_account_name || '').replace(/"/g, '""')}"`,
        `"${(row.bank_name || '').replace(/"/g, '""')}"`,
        `"${(row.account_number || '').replace(/"/g, '""')}"`,
        `"${(row.ifsc_code || '').replace(/"/g, '""')}"`,
        `"${(row.branch_name || '').replace(/"/g, '""')}"`,
        `"${(row.pan_number || '').replace(/"/g, '""')}"`,
        (row.base_amount || 0).toFixed(2),
        (row.addition_amount || 0).toFixed(2),
        (row.tds_amount || 0).toFixed(2),
        (row.deduction_amount || 0).toFixed(2),
        (row.net_payable || 0).toFixed(2),
        row.payment_status,
        `"${row.transaction_id || ''}"`,
        `"${row.payment_method || ''}"`
      ];
      csvRows.push(csvLine.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${payeeType}_Bank_Payout_Transfer_${monthFilter}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(`${payeeType} Bank Transfer CSV exported successfully!`);
  };

  // Table Columns Generator
  const getTableColumns = (payeeType) => {
    const columns = [
      {
        title: 'Payee Details',
        key: 'payee_details',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-color)', fontSize: 14 }}>
                {record.payee_name || 'N/A'}
              </span>
              <span style={{ fontSize: 12, color: '#1890ff', fontWeight: 600 }}>
                {record.payee_code} {record.qualification ? `• ${record.qualification}` : record.staff_type ? `• ${record.staff_type}` : ''}
              </span>
            </div>
          );
        }
      },
      {
        title: 'Bank & PAN Details',
        key: 'bank_details',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600 }}>
                <BankOutlined style={{ marginRight: 4, color: '#10b981' }} />
                {record.bank_name !== 'N/A' ? record.bank_name : 'No Bank Added'}
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                A/C: <strong>{record.account_number}</strong> | IFSC: <strong>{record.ifsc_code}</strong>
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                PAN: <strong>{record.pan_number}</strong>
              </div>
            </div>
          );
        }
      }
    ];

    if (payeeType === 'INSTRUCTOR') {
      columns.push({
        title: 'Session Breakdown & Inspection',
        key: 'session_breakdown',
        align: 'center',
        render: (_, rawRecord) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Tag color="green" style={{ fontWeight: 700, fontSize: 10, margin: 0 }}>
                  {rawRecord.sessions_delivered_live || 0} Live
                </Tag>
                <Tag color="purple" style={{ fontWeight: 700, fontSize: 10, margin: 0 }}>
                  {rawRecord.sessions_delivered_recorded || 0} Recorded
                </Tag>
                <Tag color="orange" style={{ fontWeight: 700, fontSize: 10, margin: 0 }}>
                  {rawRecord.sessions_cancelled_technical || 0} Tech Issue
                </Tag>
                <Tag color="red" style={{ fontWeight: 600, fontSize: 10, margin: 0 }}>
                  {rawRecord.sessions_cancelled_regular || 0} Cancelled (₹0)
                </Tag>
              </div>
              <Button
                size="small"
                type="primary"
                ghost
                icon={<UnorderedListOutlined />}
                style={{ fontSize: 11, height: 24, marginTop: 2 }}
                onClick={() => openSessionModal(rawRecord)}
              >
                Inspect Sessions ({rawRecord.sessions?.length || 0})
              </Button>
            </div>
          );
        }
      });

      columns.push({
        title: 'Bulk Pay Rules',
        key: 'bulk_pay_rules',
        align: 'center',
        render: (_, rawRecord) => {
          const effRecord = getEffectiveInstructorRecord(rawRecord);
          if (rawRecord.is_locked) {
            return (
              <div style={{ fontSize: 11, color: '#64748b' }}>
                <div>Recorded: <strong>{effRecord.isPayRecorded ? 'Paid' : 'Skipped'}</strong></div>
                <div>Tech Issue: <strong>{effRecord.isPayTechCancelled ? 'Paid' : 'Skipped'}</strong></div>
              </div>
            );
          }
          return (
            <Space direction="vertical" size="small" align="center">
              <Tooltip title="Enable to include Recorded Sessions by default">
                <Switch
                  checkedChildren="Pay Recorded"
                  unCheckedChildren="Skip Recorded"
                  checked={effRecord.isPayRecorded}
                  onChange={(checked) => {
                    setPayRecordedMap(prev => ({
                      ...prev,
                      [rawRecord.payee_id]: checked
                    }));
                  }}
                  size="small"
                />
              </Tooltip>
              <Tooltip title="Enable to include Technical / Rain Cancelled Sessions by default">
                <Switch
                  checkedChildren="Pay Tech Issue"
                  unCheckedChildren="Skip Tech Issue"
                  checked={effRecord.isPayTechCancelled}
                  onChange={(checked) => {
                    setPayTechCancelledMap(prev => ({
                      ...prev,
                      [rawRecord.payee_id]: checked
                    }));
                  }}
                  size="small"
                />
              </Tooltip>
            </Space>
          );
        }
      });
    }

    columns.push(
      {
        title: payeeType === 'INSTRUCTOR' ? 'Session Earnings' : 'Base Salary',
        key: 'base_amount',
        align: 'right',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                ₹{(record.base_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              {payeeType === 'INSTRUCTOR' && (
                <span style={{ fontSize: 11, color: '#1890ff', fontWeight: 600 }}>
                  ({record.total_sessions || 0} Payable Sessions)
                </span>
              )}
            </div>
          );
        }
      },
      {
        title: 'Additions / Expenses',
        key: 'additions',
        align: 'right',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <Tooltip title={record.addition_reason ? `Reason: ${record.addition_reason}` : 'No addition reason'}>
              <div style={{ color: record.addition_amount > 0 ? '#10b981' : 'var(--text-secondary)', fontWeight: 600 }}>
                +₹{(record.addition_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            </Tooltip>
          );
        }
      },
      {
        title: 'TDS Deduction',
        key: 'tds',
        align: 'right',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ fontSize: 12 }}>
              <div style={{ color: '#ef4444', fontWeight: 600 }}>
                -₹{(record.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>({record.tds_percent || 0}% TDS)</div>
            </div>
          );
        }
      },
      {
        title: 'Other Deductions',
        key: 'deductions',
        align: 'right',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <Tooltip title={record.deduction_reason ? `Reason: ${record.deduction_reason}` : 'No deduction reason'}>
              <div style={{ color: record.deduction_amount > 0 ? '#ef4444' : 'var(--text-secondary)', fontWeight: 600 }}>
                -₹{(record.deduction_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            </Tooltip>
          );
        }
      },
      {
        title: 'Net Payable',
        key: 'net_payable',
        align: 'right',
        sorter: (a, b) => {
          const effA = a.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(a) : a;
          const effB = b.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(b) : b;
          return effA.net_payable - effB.net_payable;
        },
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ fontWeight: 700, fontSize: 15, color: '#059669' }}>
              ₹{(record.net_payable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          );
        }
      },
      {
        title: 'Status & Txn ID',
        key: 'status_txn',
        align: 'center',
        render: (_, rawRecord) => {
          const record = payeeType === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {record.is_locked ? (
                <Tag icon={<LockOutlined />} color="success" style={{ fontWeight: 700, padding: '2px 8px' }}>
                  PAID & LOCKED
                </Tag>
              ) : (
                <Tag icon={<ClockCircleOutlined />} color="warning" style={{ fontWeight: 700, padding: '2px 8px' }}>
                  PENDING
                </Tag>
              )}
              {record.transaction_id && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#1890ff' }}>
                  Txn: {record.transaction_id}
                </span>
              )}
            </div>
          );
        }
      },
      {
        title: 'Action',
        key: 'action',
        align: 'center',
        render: (_, rawRecord) => {
          const record = rawRecord.payee_type === 'INSTRUCTOR' ? getEffectiveInstructorRecord(rawRecord) : rawRecord;
          return (
            <Space size="small">
              {!record.is_locked ? (
                <>
                  <Tooltip title="Add Expense / Allowance or Custom Deduction with Reason">
                    <Button 
                      size="small" 
                      icon={<PlusOutlined />} 
                      onClick={() => openAdjustmentModal(record)}
                    >
                      Adjust
                    </Button>
                  </Tooltip>
                  <Tooltip title="Process Salary and Enter Transaction ID">
                    <Button 
                      size="small" 
                      type="primary" 
                      icon={<CheckCircleOutlined />} 
                      onClick={() => openPayModal(record)}
                    >
                      Mark Paid
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Tag color="cyan" style={{ fontWeight: 600 }}>Locked</Tag>
              )}

              <Tooltip title="View & Print Official Salary Slip">
                <Button 
                  size="small" 
                  icon={<FileTextOutlined />} 
                  style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                  onClick={() => openSalarySlipModal(record)}
                >
                  Slip
                </Button>
              </Tooltip>
            </Space>
          );
        }
      }
    );

    return columns;
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header & Filter Controls */}
      <Card style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DollarOutlined style={{ fontSize: 24, color: '#10b981' }} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-color)' }}>
                  Payment Management & Payroll
                </h2>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Process Instructor and Staff salaries, log expenses/deductions, lock transactions, and generate salary slips.
                </span>
              </div>
            </div>
          </Col>

          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Space wrap align="center">
              <Select 
                value={academicYearFilter}
                onChange={setAcademicYearFilter}
                style={{ width: 170 }}
                placeholder="Academic Year"
              >
                <Option value="all">All Academic Years</Option>
                {academicYears.map(ay => (
                  <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                    {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                  </Option>
                ))}
              </Select>

              <Input 
                type="month" 
                value={monthFilter} 
                onChange={(e) => setMonthFilter(e.target.value || getCurrentMonthString())} 
                style={{ width: 160, borderRadius: 8 }} 
                title="Payment Month"
              />

              <Input
                placeholder="Search Name, PAN..."
                prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: 200, borderRadius: 8 }}
              />
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        {/* Global Summary Statistics & Export Actions */}
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={16}>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Statistic 
                  title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Net Payable</span>}
                  value={paymentData.summary.total_net_payable} 
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#1890ff', fontWeight: 700, fontSize: 18 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic 
                  title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Paid & Locked</span>}
                  value={paymentData.summary.total_paid} 
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#10b981', fontWeight: 700, fontSize: 18 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic 
                  title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Pending Payouts</span>}
                  value={paymentData.summary.total_pending} 
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#f97316', fontWeight: 700, fontSize: 18 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic 
                  title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Payees</span>}
                  value={paymentData.summary.total_instructors + paymentData.summary.total_staff} 
                  valueStyle={{ color: 'var(--text-color)', fontWeight: 700, fontSize: 18 }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Space wrap>
              <Button 
                icon={<DownloadOutlined />} 
                style={{ borderColor: '#10b981', color: '#10b981' }}
                onClick={() => exportBankCSV('INSTRUCTOR')}
              >
                Instructor Bank CSV
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                onClick={() => exportBankCSV('STAFF')}
              >
                Staff Bank CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Payment Tabs */}
      <Tabs defaultActiveKey="instructors" type="card">
        <Tabs.TabPane 
          tab={<span><UserOutlined /> Instructor Payment Processing ({paymentData.instructors.length})</span>} 
          key="instructors"
        >
          <Table 
            dataSource={paymentData.instructors} 
            columns={getTableColumns('INSTRUCTOR')} 
            rowKey="payee_id" 
            loading={loading} 
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane 
          tab={<span><BankOutlined /> Staff Salary Processing ({paymentData.staff.length})</span>} 
          key="staff"
        >
          <Table 
            dataSource={paymentData.staff} 
            columns={getTableColumns('STAFF')} 
            rowKey="payee_id" 
            loading={loading} 
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane 
          tab={<span><LockOutlined /> Processed & Locked History</span>} 
          key="history"
        >
          <Table 
            dataSource={[...paymentData.instructors, ...paymentData.staff].filter(r => r.is_locked)} 
            columns={getTableColumns('ALL')} 
            rowKey={(r) => `${r.payee_type}_${r.payee_id}`} 
            loading={loading} 
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 15, showSizeChanger: true }}
          />
        </Tabs.TabPane>
      </Tabs>

      {/* Modal 1: Adjustment (Expenses / Deductions) Form */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlusOutlined style={{ color: '#1890ff' }} />
            <span>Add Expenses / Deductions for {activeRecord?.payee_name}</span>
          </div>
        }
        open={isAdjustmentModalOpen}
        onCancel={() => setIsAdjustmentModalOpen(false)}
        onOk={() => adjustmentForm.submit()}
        destroyOnClose
      >
        <Form form={adjustmentForm} layout="vertical" onFinish={handleSaveAdjustment}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="base_amount" label="Base / Session Amount (₹)">
                <InputNumber style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tds_percent" label="TDS Deduction (%)">
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="addition_amount" label="Additions / Expense (₹)">
                <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 1500" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="addition_reason" label="Addition / Expense Reason">
                <Input placeholder="e.g. Travel Allowance / Bonus" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="deduction_amount" label="Custom Deduction (₹)">
                <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 500" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deduction_reason" label="Deduction Reason">
                <Input placeholder="e.g. Unexcused Absence / Equipment damage" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal 2: Process Pay & Enter Transaction ID */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleOutlined style={{ color: '#10b981' }} />
            <span>Process Payment & Lock Transaction for {activeRecord?.payee_name}</span>
          </div>
        }
        open={isPayModalOpen}
        onCancel={() => setIsPayModalOpen(false)}
        onOk={() => payForm.submit()}
        destroyOnClose
      >
        {activeRecord && (
          <div style={{ marginBottom: 16, padding: 12, background: 'rgba(16, 185, 129, 0.08)', border: '1px solid #10b981', borderRadius: 8 }}>
            <Row justify="space-between" align="middle">
              <div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Net Amount Payable:</span>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>
                  ₹{activeRecord.net_payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <Tag color="purple" style={{ fontSize: 13, padding: '4px 10px', fontWeight: 600 }}>
                {activeRecord.payee_type}: {activeRecord.payee_code}
              </Tag>
            </Row>
            <div style={{ fontSize: 12, marginTop: 6, color: '#64748b' }}>
              Bank: <strong>{activeRecord.bank_name}</strong> | A/C: <strong>{activeRecord.account_number}</strong> | IFSC: <strong>{activeRecord.ifsc_code}</strong>
            </div>
          </div>
        )}

        <Form form={payForm} layout="vertical" onFinish={handleProcessPay}>
          <Form.Item 
            name="transaction_id" 
            label="Transaction ID / UTR Reference No." 
            rules={[{ required: true, message: 'Transaction ID is required to mark salary as paid!' }]}
          >
            <Input placeholder="e.g. UTR129847192847 / NEFT98124719" prefix={<CreditCardOutlined style={{ color: '#10b981' }} />} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
                <Select placeholder="Select Method">
                  <Option value="Bank Transfer (NEFT)">Bank Transfer (NEFT)</Option>
                  <Option value="Bank Transfer (RTGS)">Bank Transfer (RTGS)</Option>
                  <Option value="IMPS / UPI">IMPS / UPI</Option>
                  <Option value="Cheque">Cheque</Option>
                  <Option value="Cash">Cash</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} showTime format="DD-MM-YYYY HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="paid_by_name" label="Paid By / Authorization">
            <Input placeholder="e.g. Accounts Department / Admin Officer" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal 3: Official Printable Salary Slip Component */}
      <Modal
        title="Official Salary Slip Preview"
        open={isSalarySlipModalOpen}
        onCancel={() => setIsSalarySlipModalOpen(false)}
        width={800}
        footer={[
          <Button 
            key="config" 
            icon={<SettingOutlined />} 
            onClick={() => setShowSlipHeaderConfig(!showSlipHeaderConfig)}
          >
            {showSlipHeaderConfig ? 'Hide Header Settings' : 'Customize Header & Logo'}
          </Button>,
          <Button key="close" onClick={() => setIsSalarySlipModalOpen(false)}>
            Close
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={() => {
              const printContent = slipPrintRef.current.innerHTML;
              const printWindow = window.open('', '', 'height=800,width=900');
              printWindow.document.write('<html><head><title>Salary Slip</title>');
              printWindow.document.write('<style>');
              printWindow.document.write(`
                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1e293b; }
                .slip-container { border: 2px solid #0f172a; padding: 24px; border-radius: 12px; }
                .slip-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 16px; }
                .slip-title { font-size: 20px; font-weight: 800; color: #0f172a; text-transform: uppercase; margin: 0; }
                .info-table, .calc-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
                .info-table td { padding: 6px 10px; font-size: 13px; }
                .calc-table th, .calc-table td { border: 1px solid #cbd5e1; padding: 8px 12px; font-size: 13px; }
                .calc-table th { background: #f8fafc; font-weight: 700; text-align: left; }
                .net-banner { background: #ecfdf5; border: 1px solid #10b981; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
                .footer-box { border-top: 2px dashed #cbd5e1; padding-top: 16px; margin-top: 24px; display: flex; justify-content: space-between; font-size: 12px; }
              `);
              printWindow.document.write('</style></head><body>');
              printWindow.document.write(printContent);
              printWindow.document.write('</body></html>');
              printWindow.document.close();
              printWindow.focus();
              setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
            }}
          >
            Print / Save PDF
          </Button>
        ]}
      >
        {/* Custom Header & Logo Customization Box */}
        {showSlipHeaderConfig && (
          <Card 
            size="small" 
            title="Custom Salary Slip Header & Logo Configuration" 
            style={{ marginBottom: 16, background: 'var(--panel-color)', border: '1px solid var(--border-color)', color: 'var(--text-color)' }}
          >
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Form.Item label="Institution / Organization Name" style={{ marginBottom: 8 }}>
                  <Input 
                    value={slipOrgName} 
                    onChange={(e) => setSlipOrgName(e.target.value)} 
                    placeholder="e.g. VEDIKA ACADEMY" 
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Slip Subtitle / Voucher Title" style={{ marginBottom: 8 }}>
                  <Input 
                    value={slipSubtitle} 
                    onChange={(e) => setSlipSubtitle(e.target.value)} 
                    placeholder="e.g. Official Payslip / Payment Advice Voucher" 
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label="Custom Logo Image URL or Local File Upload" style={{ marginBottom: 8 }}>
                  <Input 
                    value={slipCustomLogo} 
                    onChange={(e) => setSlipCustomLogo(e.target.value)} 
                    placeholder="Paste Image URL or click upload ->" 
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 8 }}>
                <input 
                  type="file" 
                  id="slip-logo-file-input" 
                  accept="image/*" 
                  style={{ display: 'none' }} 
                  onChange={handleLogoFileUpload} 
                />
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => document.getElementById('slip-logo-file-input').click()}
                >
                  Upload Logo
                </Button>
                {slipCustomLogo && (
                  <Button 
                    icon={<UndoOutlined />} 
                    title="Reset to default logo" 
                    onClick={() => setSlipCustomLogo('')}
                  >
                    Reset
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        )}

        {slipRecord && (
          <div ref={slipPrintRef} style={{ padding: 16 }}>
            <div style={{ border: '2px solid var(--border-color)', borderRadius: 12, padding: 24, background: 'var(--panel-color)', color: 'var(--text-color)' }}>
              
              {/* Custom Header & Logo Section */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--border-color)', paddingBottom: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img 
                    src={slipCustomLogo || logoGif} 
                    alt="Logo" 
                    style={{ height: 52, maxWidth: 180, objectFit: 'contain' }} 
                  />
                  <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-color)' }}>
                      {slipOrgName || 'VEDIKA ACADEMY'}
                    </h2>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {slipSubtitle || 'Official Payslip / Payment Advice Voucher'}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>SALARY SLIP</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Month: {slipRecord.payment_month}</div>
                </div>
              </div>

              {/* Employee Details Box */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, background: 'var(--bg-color)', borderRadius: 8 }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px 12px', fontSize: 13, width: '20%', color: 'var(--text-secondary)' }}>Payee Name:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, width: '30%', fontWeight: 700, color: 'var(--text-color)' }}>{slipRecord.payee_name}</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, width: '20%', color: 'var(--text-secondary)' }}>Payee Code / ID:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, width: '30%', fontWeight: 700, color: 'var(--text-color)' }}>{slipRecord.payee_code}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>Designation / Role:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{slipRecord.qualification || slipRecord.staff_type || slipRecord.payee_type}</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>PAN Number:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{slipRecord.pan_number}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>Bank Name:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{slipRecord.bank_name}</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>Account No:</td>
                    <td style={{ padding: '6px 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{slipRecord.account_number} (IFSC: {slipRecord.ifsc_code})</td>
                  </tr>
                </tbody>
              </table>

              {/* Instructor Session Details Box */}
              {slipRecord.payee_type === 'INSTRUCTOR' && (
                <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-color)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                  <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 8, color: 'var(--text-secondary)' }}>
                    Session Count & Payment Breakdown
                  </div>
                  <Row gutter={[12, 12]}>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Live Sessions:</div>
                      <strong style={{ color: '#10b981', fontSize: 13 }}>
                        {slipRecord.sessions_delivered_live || 0} Sessions (₹{(slipRecord.live_session_amount || 0).toLocaleString('en-IN')})
                      </strong>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Recorded Sessions:</div>
                      <strong style={{ color: slipRecord.isPayRecorded ? '#a855f7' : 'var(--text-secondary)', fontSize: 13 }}>
                        {slipRecord.sessions_delivered_recorded || 0} Sessions ({slipRecord.isPayRecorded ? 'Paid' : 'Skipped'})
                      </strong>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Tech/Rain Issue:</div>
                      <strong style={{ color: slipRecord.isPayTechCancelled ? '#f97316' : 'var(--text-secondary)', fontSize: 13 }}>
                        {slipRecord.sessions_cancelled_technical || 0} Sessions ({slipRecord.isPayTechCancelled ? 'Paid' : 'Skipped'})
                      </strong>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Regular Cancelled:</div>
                      <strong style={{ color: '#ef4444', fontSize: 13 }}>
                        {slipRecord.sessions_cancelled_regular || 0} Sessions (No Pay)
                      </strong>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Earnings & Deductions Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-color)', color: 'var(--text-color)' }}>
                    <th style={{ padding: '8px 12px', textTransform: 'uppercase', fontSize: 12, textAlign: 'left', width: '50%', border: '1px solid var(--border-color)' }}>Earnings & Additions</th>
                    <th style={{ padding: '8px 12px', textTransform: 'uppercase', fontSize: 12, textAlign: 'left', width: '50%', border: '1px solid var(--border-color)' }}>Deductions & Tax</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px', verticalAlign: 'top', color: 'var(--text-color)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span>Base Salary / Session Earnings ({slipRecord.total_sessions || 0} Sessions):</span>
                        <strong>₹{slipRecord.base_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                      </div>
                      {slipRecord.addition_amount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981' }}>
                          <span>Additions / Bonus ({slipRecord.addition_reason || 'Allowance'}):</span>
                          <strong>+₹{slipRecord.addition_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      )}
                    </td>
                    <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px', verticalAlign: 'top', color: 'var(--text-color)' }}>
                      {slipRecord.tds_amount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444', marginBottom: 6 }}>
                          <span>TDS Deduction ({slipRecord.tds_percent}%):</span>
                          <strong>-₹{slipRecord.tds_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      )}
                      {slipRecord.deduction_amount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444' }}>
                          <span>Other Deduction ({slipRecord.deduction_reason || 'Deduction'}):</span>
                          <strong>-₹{slipRecord.deduction_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr style={{ background: 'var(--bg-color)', fontWeight: 700, color: 'var(--text-color)' }}>
                    <td style={{ border: '1px solid var(--border-color)', padding: '8px 12px', textAlign: 'right' }}>
                      Total Gross Earnings: ₹{(slipRecord.base_amount + slipRecord.addition_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: '1px solid var(--border-color)', padding: '8px 12px', textAlign: 'right', color: '#ef4444' }}>
                      Total Deductions: ₹{(slipRecord.tds_amount + slipRecord.deduction_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Net Amount Banner */}
              <div style={{ background: 'rgba(16, 185, 129, 0.12)', border: '1px solid #10b981', padding: '12px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600, display: 'block' }}>NET PAYABLE AMOUNT:</span>
                  <strong style={{ fontSize: 13, color: 'var(--text-color)' }}>{numberToWords(Math.round(slipRecord.net_payable))}</strong>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>
                  ₹{slipRecord.net_payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Transaction & Paid By Details Footer */}
              <div style={{ borderTop: '2px dashed var(--border-color)', paddingTop: 16, marginTop: 16, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-color)' }}>
                <div>
                  <div>Payment Status: <strong style={{ color: slipRecord.is_locked ? '#10b981' : '#f97316' }}>{slipRecord.payment_status}</strong></div>
                  <div>Transaction ID / UTR: <strong>{slipRecord.transaction_id || 'N/A'}</strong></div>
                  <div>Payment Method: <strong>{slipRecord.payment_method || 'N/A'}</strong></div>
                  <div>Payment Date: <strong>{slipRecord.payment_date ? dayjs(slipRecord.payment_date).format('DD-MM-YYYY HH:mm') : 'N/A'}</strong></div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div>Paid By: <strong>{slipRecord.paid_by_name || 'Accounts Department'}</strong></div>
                  <div style={{ marginTop: 24, borderTop: '1px solid var(--text-secondary)', paddingTop: 4, width: 160, marginLeft: 'auto', textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>
                    Authorized Signature
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}
      </Modal>

      {/* Modal 4: Session-Wise Payment Selection & Inspection Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UnorderedListOutlined style={{ color: '#1890ff' }} />
            <span>Session-Wise Payment Selection for {sessionModalInstructor?.payee_name} ({monthFilter})</span>
          </div>
        }
        open={isSessionModalOpen}
        onCancel={() => setIsSessionModalOpen(false)}
        width={900}
        footer={[
          <Button 
            key="reset" 
            onClick={() => {
              const newOverride = { ...sessionOverrideMap };
              (sessionModalInstructor?.sessions || []).forEach(s => {
                delete newOverride[s.daily_session_id];
              });
              setSessionOverrideMap(newOverride);
              message.info('Reset sessions for this instructor to global/type defaults.');
            }}
          >
            Reset to Default Rules
          </Button>,
          <Button 
            key="select-all" 
            type="primary" 
            ghost
            onClick={() => {
              const newOverride = { ...sessionOverrideMap };
              (sessionModalInstructor?.sessions || []).forEach(s => {
                newOverride[s.daily_session_id] = true;
              });
              setSessionOverrideMap(newOverride);
              message.success('Marked all sessions as Paid.');
            }}
          >
            Select All Paid
          </Button>,
          <Button 
            key="deselect-all" 
            danger
            onClick={() => {
              const newOverride = { ...sessionOverrideMap };
              (sessionModalInstructor?.sessions || []).forEach(s => {
                newOverride[s.daily_session_id] = false;
              });
              setSessionOverrideMap(newOverride);
              message.warning('Deselected all sessions for payment.');
            }}
          >
            Deselect All
          </Button>,
          <Button key="close" type="primary" onClick={() => setIsSessionModalOpen(false)}>
            Done
          </Button>
        ]}
      >
        {sessionModalInstructor && (
          <div>
            <Card size="small" style={{ marginBottom: 16, background: 'var(--panel-color)', border: '1px solid var(--border-color)' }}>
              <Row justify="space-between" align="middle">
                <div>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Instructor:</span>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-color)' }}>
                    {sessionModalInstructor.payee_name} ({sessionModalInstructor.payee_code})
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {(() => {
                    const eff = getEffectiveInstructorRecord(sessionModalInstructor);
                    return (
                      <div>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Effective Base Amount:</span>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981' }}>
                          ₹{(eff.base_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#1890ff', marginLeft: 8 }}>
                            ({eff.total_sessions || 0} Sessions Paid)
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </Row>
            </Card>

            <Table 
              dataSource={sessionModalInstructor.sessions || []}
              rowKey="daily_session_id"
              pagination={{ pageSize: 8, showSizeChanger: true }}
              size="small"
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'daily_session_date',
                  key: 'date',
                  render: (d) => <span style={{ fontWeight: 600 }}>{dayjs(d).format('DD MMM YYYY')}</span>
                },
                {
                  title: 'Standard & Subject',
                  key: 'std_sub',
                  render: (_, s) => (
                    <div>
                      <div style={{ fontWeight: 600, color: '#1890ff' }}>{s.standard_name}</div>
                      <Tag color="cyan" style={{ fontSize: 10 }}>{s.subjects_name}</Tag>
                    </div>
                  )
                },
                {
                  title: 'Lesson / Topic',
                  dataIndex: 'daily_session_part',
                  key: 'topic',
                  render: (t) => <span style={{ fontSize: 12 }}>{t || '—'}</span>
                },
                {
                  title: 'Session Type',
                  key: 'type',
                  align: 'center',
                  render: (_, s) => {
                    if (s.is_cancelled) {
                      if (s.is_technical_cancelled) {
                        return <Tag color="orange" style={{ fontWeight: 700 }}>TECH / RAIN ISSUE</Tag>;
                      }
                      return <Tag color="red" style={{ fontWeight: 700 }}>CANCELLED</Tag>;
                    }
                    if (s.is_recorded) {
                      return <Tag color="purple" style={{ fontWeight: 700 }}>RECORDED</Tag>;
                    }
                    return <Tag color="green" style={{ fontWeight: 700 }}>LIVE CLASS</Tag>;
                  }
                },
                {
                  title: 'Rate & Units',
                  key: 'units_rate',
                  align: 'right',
                  render: (_, s) => (
                    <div style={{ fontSize: 12 }}>
                      <div><strong>{s.session_count || 1} Unit</strong></div>
                      <span style={{ color: '#64748b' }}>₹{s.session_rate}/unit</span>
                    </div>
                  )
                },
                {
                  title: 'Session Amt',
                  key: 'amount',
                  align: 'right',
                  render: (_, s) => {
                    const amt = (s.session_count || 1) * (s.session_rate || 0);
                    return (
                      <span style={{ fontWeight: 700, color: amt > 0 ? '#059669' : '#dc2626' }}>
                        ₹{amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    );
                  }
                },
                {
                  title: 'Session Pay Action',
                  key: 'pay_action',
                  align: 'center',
                  render: (_, s) => {
                    const paid = isSessionPaid(s, sessionModalInstructor.payee_id);
                    const isOverridden = sessionOverrideMap[s.daily_session_id] !== undefined;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Switch
                          checkedChildren="Paid"
                          unCheckedChildren="Skip"
                          checked={paid}
                          disabled={sessionModalInstructor.is_locked}
                          onChange={(checked) => {
                            setSessionOverrideMap(prev => ({
                              ...prev,
                              [s.daily_session_id]: checked
                            }));
                          }}
                          size="small"
                        />
                        {isOverridden && (
                          <span style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 600 }}>
                            (Manual Override)
                          </span>
                        )}
                      </div>
                    );
                  }
                }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentManagementPage;
